import { createWgs84CoordinateTransform } from '../../crs/coordinateTransform';
import { CANONICAL_CRS_IDENTIFIER, type CrsMetadata } from '../../crs/model';
import type { CatalogueResolvedCrs } from '../../crs/epsgCatalogue';
import type {
  FeatureProperties,
  FieldDefinition,
  SourceWarning,
  TableScalarValue,
  VectorFeature,
  VectorFeatureCollection,
} from '../model';
import { inspectGeoJson } from './geojsonInspection';
import type { ImportedVectorData } from './model';
import { TABULAR_INFERENCE_SAMPLE_SIZE, type ImportedTableData } from './tabularImport';

export interface CoordinateFieldSuggestions {
  readonly likelyGeographic: boolean;
  readonly strong: boolean;
  readonly xField: string;
  readonly yField: string;
}

export type SpreadsheetSourceCrs =
  { readonly kind: 'wgs84' } | { readonly kind: 'resolved'; readonly value: CatalogueResolvedCrs };

export interface PointCreationPreview {
  readonly invalidRowCount: number;
  readonly totalRowCount: number;
  readonly validPointCount: number;
}

export type SpreadsheetPointResult =
  | {
      readonly ok: true;
      readonly value: {
        readonly imported: ImportedVectorData;
        readonly preview: PointCreationPreview;
      };
    }
  | {
      readonly ok: false;
      readonly error: {
        readonly code: 'invalid-fields' | 'no-valid-points';
        readonly message: string;
      };
      readonly preview: PointCreationPreview;
    };

const X_GEOGRAPHIC_NAMES = new Set(['longitude', 'lon', 'long', 'lng']);
const Y_GEOGRAPHIC_NAMES = new Set(['latitude', 'lat']);
const X_PROJECTED_NAMES = new Set(['easting', 'east']);
const Y_PROJECTED_NAMES = new Set(['northing', 'north']);
const STRICT_COORDINATE = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

function normalizedFieldName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function coordinateNumber(value: TableScalarValue | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!STRICT_COORDINATE.test(trimmed)) return null;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
}

function numericRatio(table: ImportedTableData, column: number): number {
  const limit = Math.min(table.rows.length, TABULAR_INFERENCE_SAMPLE_SIZE);
  let populated = 0;
  let numeric = 0;
  for (let index = 0; index < limit; index += 1) {
    const value = table.rows[index]?.[column];
    if (value === null || value === undefined || value === '') continue;
    populated += 1;
    if (coordinateNumber(value) !== null) numeric += 1;
  }
  return populated === 0 ? 0 : numeric / populated;
}

function nameScore(name: string, axis: 'x' | 'y'): number {
  const normalized = normalizedFieldName(name);
  if (axis === 'x' && X_GEOGRAPHIC_NAMES.has(normalized)) return 100;
  if (axis === 'y' && Y_GEOGRAPHIC_NAMES.has(normalized)) return 100;
  if (axis === 'x' && X_PROJECTED_NAMES.has(normalized)) return 80;
  if (axis === 'y' && Y_PROJECTED_NAMES.has(normalized)) return 80;
  if (normalized === axis) return 65;
  return 0;
}

function pairLooksGeographic(table: ImportedTableData, xColumn: number, yColumn: number): boolean {
  const limit = Math.min(table.rows.length, TABULAR_INFERENCE_SAMPLE_SIZE);
  let numericPairs = 0;
  let geographicPairs = 0;
  for (let index = 0; index < limit; index += 1) {
    const x = coordinateNumber(table.rows[index]?.[xColumn]);
    const y = coordinateNumber(table.rows[index]?.[yColumn]);
    if (x === null || y === null) continue;
    numericPairs += 1;
    if (x >= -180 && x <= 180 && y >= -90 && y <= 90) geographicPairs += 1;
  }
  return numericPairs > 0 && geographicPairs / numericPairs >= 0.9;
}

function fieldsExplicitlyLookReversed(
  table: ImportedTableData,
  xColumn: number,
  yColumn: number,
): boolean {
  return (
    Y_GEOGRAPHIC_NAMES.has(normalizedFieldName(table.fields[xColumn]?.name ?? '')) &&
    X_GEOGRAPHIC_NAMES.has(normalizedFieldName(table.fields[yColumn]?.name ?? ''))
  );
}

export function hasLikelyWgs84AxisReversal(
  table: ImportedTableData,
  xField: string,
  yField: string,
): boolean {
  const xColumn = table.fields.findIndex((field) => field.name === xField);
  const yColumn = table.fields.findIndex((field) => field.name === yField);
  if (xColumn < 0 || yColumn < 0 || xColumn === yColumn) return false;

  const limit = Math.min(table.rows.length, TABULAR_INFERENCE_SAMPLE_SIZE);
  let usablePairCount = 0;
  let currentPlausibleCount = 0;
  let reversedPlausibleCount = 0;
  let reversalEvidenceCount = 0;
  for (let index = 0; index < limit; index += 1) {
    const x = coordinateNumber(table.rows[index]?.[xColumn]);
    const y = coordinateNumber(table.rows[index]?.[yColumn]);
    if (x === null || y === null) continue;
    usablePairCount += 1;
    const currentPlausible = x >= -180 && x <= 180 && y >= -90 && y <= 90;
    const reversedPlausible = y >= -180 && y <= 180 && x >= -90 && x <= 90;
    if (currentPlausible) currentPlausibleCount += 1;
    if (reversedPlausible) reversedPlausibleCount += 1;
    if (!currentPlausible && reversedPlausible && (y < -90 || y > 90)) {
      reversalEvidenceCount += 1;
    }
  }
  if (usablePairCount === 0) return false;

  const consistencyThreshold = fieldsExplicitlyLookReversed(table, xColumn, yColumn) ? 0.8 : 0.9;
  return (
    currentPlausibleCount / usablePairCount <= 0.1 &&
    reversedPlausibleCount / usablePairCount >= consistencyThreshold &&
    reversalEvidenceCount / usablePairCount >= consistencyThreshold
  );
}

export function suggestCoordinateFields(table: ImportedTableData): CoordinateFieldSuggestions {
  const ranked = (axis: 'x' | 'y') =>
    table.fields
      .map((field, index) => ({
        field,
        index,
        ratio: numericRatio(table, index),
        score: nameScore(field.name, axis),
      }))
      .filter((candidate) => candidate.score > 0 && candidate.ratio >= 0.5)
      .sort(
        (left, right) =>
          right.score - left.score || right.ratio - left.ratio || left.index - right.index,
      );
  const xCandidates = ranked('x');
  const yCandidates = ranked('y');
  const x = xCandidates[0];
  const y = yCandidates.find((candidate) => candidate.index !== x?.index) ?? yCandidates[0];
  if (!x || !y || x.index === y.index) {
    return { likelyGeographic: false, strong: false, xField: '', yField: '' };
  }
  const likelyGeographic =
    X_GEOGRAPHIC_NAMES.has(normalizedFieldName(x.field.name)) &&
    Y_GEOGRAPHIC_NAMES.has(normalizedFieldName(y.field.name)) &&
    pairLooksGeographic(table, x.index, y.index);
  return {
    likelyGeographic,
    strong: likelyGeographic && x.score === 100 && y.score === 100,
    xField: x.field.name,
    yField: y.field.name,
  };
}

function sourceMetadata(sourceCrs: SpreadsheetSourceCrs): CrsMetadata {
  if (sourceCrs.kind === 'resolved') return sourceCrs.value.metadata;
  return {
    status: 'user-confirmed',
    identifier: CANONICAL_CRS_IDENTIFIER,
    name: 'WGS 84 (longitude/latitude)',
    determination: 'user',
    canonicalIdentifier: CANONICAL_CRS_IDENTIFIER,
    transformation: 'none',
  };
}

function propertiesForRow(
  fields: readonly FieldDefinition[],
  row: readonly TableScalarValue[],
): FeatureProperties {
  return Object.fromEntries(
    fields.map((field, index) => [field.name, row[index] ?? null]),
  ) as Record<string, TableScalarValue>;
}

function canonicalCoordinate(
  x: number,
  y: number,
  sourceCrs: SpreadsheetSourceCrs,
  transform: ((position: readonly number[]) => number[]) | null,
): readonly [number, number] | null {
  if (sourceCrs.kind === 'wgs84') {
    return x >= -180 && x <= 180 && y >= -90 && y <= 90 ? [x, y] : null;
  }
  try {
    const output = transform?.([x, y]);
    const longitude = output?.[0];
    const latitude = output?.[1];
    return longitude !== undefined &&
      latitude !== undefined &&
      Number.isFinite(longitude) &&
      Number.isFinite(latitude) &&
      longitude >= -180 &&
      longitude <= 180 &&
      latitude >= -90 &&
      latitude <= 90
      ? [longitude, latitude]
      : null;
  } catch {
    return null;
  }
}

function pointFeatures(
  table: ImportedTableData,
  xField: string,
  yField: string,
  sourceCrs: SpreadsheetSourceCrs,
): { readonly features: readonly VectorFeature[]; readonly preview: PointCreationPreview } | null {
  const xColumn = table.fields.findIndex((field) => field.name === xField);
  const yColumn = table.fields.findIndex((field) => field.name === yField);
  if (xColumn < 0 || yColumn < 0 || xColumn === yColumn) return null;
  let transform: ((position: readonly number[]) => number[]) | null;
  try {
    transform =
      sourceCrs.kind === 'resolved'
        ? createWgs84CoordinateTransform(sourceCrs.value.definition)
        : null;
  } catch {
    return {
      features: [],
      preview: {
        totalRowCount: table.rows.length,
        validPointCount: 0,
        invalidRowCount: table.rows.length,
      },
    };
  }
  const features: VectorFeature[] = [];
  for (const row of table.rows) {
    const x = coordinateNumber(row[xColumn]);
    const y = coordinateNumber(row[yColumn]);
    if (x === null || y === null) continue;
    const coordinates = canonicalCoordinate(x, y, sourceCrs, transform);
    if (!coordinates) continue;
    features.push({
      type: 'Feature',
      properties: propertiesForRow(table.fields, row),
      geometry: { type: 'Point', coordinates: [...coordinates] },
    });
  }
  return {
    features,
    preview: {
      totalRowCount: table.rows.length,
      validPointCount: features.length,
      invalidRowCount: table.rows.length - features.length,
    },
  };
}

export function previewSpreadsheetPoints(
  table: ImportedTableData,
  xField: string,
  yField: string,
  sourceCrs: SpreadsheetSourceCrs,
): PointCreationPreview {
  return (
    pointFeatures(table, xField, yField, sourceCrs)?.preview ?? {
      totalRowCount: table.rows.length,
      validPointCount: 0,
      invalidRowCount: table.rows.length,
    }
  );
}

export function createSpreadsheetPointLayer(
  table: ImportedTableData,
  xField: string,
  yField: string,
  sourceCrs: SpreadsheetSourceCrs,
): SpreadsheetPointResult {
  const result = pointFeatures(table, xField, yField, sourceCrs);
  if (!result) {
    return {
      ok: false,
      error: { code: 'invalid-fields', message: 'Choose different X and Y coordinate fields.' },
      preview: {
        totalRowCount: table.rows.length,
        validPointCount: 0,
        invalidRowCount: table.rows.length,
      },
    };
  }
  if (result.features.length === 0) {
    return {
      ok: false,
      error: {
        code: 'no-valid-points',
        message: 'No valid coordinate pairs were found. Change the fields or coordinate system.',
      },
      preview: result.preview,
    };
  }

  const data: VectorFeatureCollection = {
    type: 'FeatureCollection',
    features: [...result.features],
  };
  const inspection = inspectGeoJson(data);
  const warnings: SourceWarning[] =
    result.preview.invalidRowCount === 0
      ? []
      : [
          {
            code: 'invalid-coordinate-rows',
            message: `Created ${result.preview.validPointCount.toLocaleString()} ${result.preview.validPointCount === 1 ? 'point' : 'points'}. ${result.preview.invalidRowCount.toLocaleString()} ${result.preview.invalidRowCount === 1 ? 'row was' : 'rows were'} skipped because coordinates were missing, invalid, out of range, or could not be transformed.`,
          },
        ];

  return {
    ok: true,
    value: {
      preview: result.preview,
      imported: {
        ...inspection,
        data,
        warnings,
        suggestedLayerName: table.suggestedTableName,
        provenance: {
          originalFileName: table.provenance.originalFileName,
          originalFormat: table.provenance.originalFormat,
          ...(table.provenance.worksheetName
            ? { datasetName: table.provenance.worksheetName }
            : {}),
        },
        crs: sourceMetadata(sourceCrs),
      },
    },
  };
}
